/**
 * Module PDF partagé — génère le contenu d'une feuille d'heures hebdomadaire.
 *
 * Utilisé par :
 *  - useTimesheetPDF.ts (soumission employé)
 *  - useValidateActions.ts (régénération admin)
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  getWeeklyTheoreticalHours,
  isEvenWeek,
  calculateWeeklyOvertime,
  resolveWeeklyHours,
} from './overtimeUtils';
import { getZoneRank } from './zoneUtils';

interface JsPDFWithAutoTable extends jsPDF {
  lastAutoTable: { finalY: number };
}

// --- Types d'entrée normalisés ---

export interface PDFProjectRow {
  projectName: string;
  dayHours: Record<string, number>;  // dateKey → heures
}

export interface PDFIndemnityStats {
  totalMeals: number;
  totalTransport: number;
  totalTrajet: number;
  zonesCount: Record<string, number>;   // zone → nb jours
}

export interface PDFGDStats {
  totalGDDays: number;
  totalGDAmount: number;
  totalGDTravelHours: number;
  gdProjectName: string;
  gdDailyRate: number;
}

export interface PDFHoursStats {
  totalNormal: number;
  totalSaturday: number;
  totalSunday: number;
}

export interface BuildTimesheetPDFParams {
  projectRows: PDFProjectRow[];
  weekDays: Date[];
  holidays: string[];
  trainingDays: string[];             // dateKeys des jours de formation

  indemnityStats: PDFIndemnityStats;
  gdStats: PDFGDStats;
  hoursStats: PDFHoursStats;

  employeeName: string;
  weekNumber: number;
  year: number;
  dateLabel: string;                  // ex. "Généré le : ..." ou "Régénéré le : ..."

  isBE35h: boolean;
  customWeeklyHours: number | null;
  overrideWeeklyHours: number | null;

  employeeSignature: string | null;   // data URL ou null
  logoData: string | null;
  directionSignData: string | null;
}

// --- Helper ---
const fmtHours = (v: number): string => {
  const rounded = Math.round(v * 100) / 100;
  return rounded + 'h';
};

const dateKeyLocal = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * Construit un document jsPDF complet pour une feuille d'heures hebdomadaire.
 * Fonction pure — ne fait aucun I/O (pas de Supabase, pas de Storage).
 */
export function buildTimesheetPDF(params: BuildTimesheetPDFParams): jsPDF {
  const {
    projectRows, weekDays, holidays, trainingDays,
    indemnityStats, gdStats, hoursStats,
    employeeName, weekNumber, year, dateLabel,
    isBE35h, customWeeklyHours, overrideWeeklyHours,
    employeeSignature, logoData, directionSignData,
  } = params;

  const doc = new jsPDF({ compress: true });

  // --- HEADER ---
  if (logoData) {
    doc.addImage(logoData, 'PNG', 14, 8, 45, 0);
  }
  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  doc.text("FEUILLE D'HEURES HEBDOMADAIRE", 14, 35);
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Semaine ${weekNumber} - Année ${year}`, 14, 41);
  doc.text(`Employé : ${employeeName}`, 14, 46);
  doc.text(dateLabel, 14, 51);

  // --- TABLE PROJETS ---
  const tableColumn = [
    "Chantier",
    ...weekDays.map(d => d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })),
    "Total"
  ];

  const tableRows = projectRows.map(row => {
    let rowTotal = 0;
    const daysData = weekDays.map(day => {
      const val = row.dayHours[dateKeyLocal(day)] || 0;
      rowTotal += val;
      return val > 0 ? fmtHours(val) : '-';
    });
    return [row.projectName, ...daysData, fmtHours(rowTotal)];
  });

  if (trainingDays.length > 0) {
    const formDaysData = weekDays.map(day =>
      trainingDays.includes(dateKeyLocal(day)) ? '7h' : '-'
    );
    tableRows.push(['FORMATION / STAGE', ...formDaysData, fmtHours(trainingDays.length * 7)]);
  }

  // Ligne totaux journaliers
  const dailyTotals = weekDays.map(day => {
    const dKey = dateKeyLocal(day);
    let sum = 0;
    projectRows.forEach(row => { sum += row.dayHours[dKey] || 0; });
    if (trainingDays.includes(dKey)) sum += 7;
    return sum;
  });
  const grandTotal = dailyTotals.reduce((a, b) => a + b, 0);
  const footRow = [
    'Total du jour',
    ...dailyTotals.map(v => v > 0 ? fmtHours(v) : '-'),
    fmtHours(grandTotal),
  ];

  autoTable(doc, {
    head: [tableColumn],
    body: tableRows,
    foot: [footRow],
    startY: 60,
    theme: 'grid',
    headStyles: { fillColor: [41, 128, 185], fontSize: 8 },
    footStyles: { fillColor: [235, 245, 251], textColor: [24, 90, 140], fontStyle: 'bold', fontSize: 8 },
    styles: { fontSize: 7, cellPadding: 1.5 },
    columnStyles: { 0: { cellWidth: 40, fontStyle: 'bold' } }
  });

  // --- CALCULS THÉORIQUES & HEURES SUPPLÉMENTAIRES ---
  const { totalNormal, totalSaturday, totalSunday } = hoursStats;
  const totalHoursWk = parseFloat((totalNormal + totalSaturday + totalSunday).toFixed(2));

  let holidaysInWeekCount = 0;
  weekDays.forEach(day => {
    const dKey = dateKeyLocal(day);
    const dow = day.getDay();
    if (dow >= 1 && dow <= 5 && holidays.includes(dKey)) {
      holidaysInWeekCount++;
    }
  });

  const hasGD = gdStats.totalGDDays > 0;
  let theoreticalHours: number;
  let theoreticalLabel: string;

  if (hasGD) {
    const base = 35;
    const deducted = holidaysInWeekCount * 7;
    theoreticalHours = base - deducted;
    theoreticalLabel = holidaysInWeekCount > 0
      ? `GD: 35h - ${holidaysInWeekCount}×7h férié = ${theoreticalHours}h`
      : 'GD: 35h (Lun-Jeu)';
  } else if (resolveWeeklyHours(overrideWeeklyHours, customWeeklyHours)) {
    const effectiveBase = resolveWeeklyHours(overrideWeeklyHours, customWeeklyHours)!;
    const deducted = holidaysInWeekCount * 7;
    theoreticalHours = effectiveBase - deducted;
    theoreticalLabel = holidaysInWeekCount > 0
      ? `Intérim: ${effectiveBase}h - ${holidaysInWeekCount}×7h férié = ${theoreticalHours}h`
      : `Intérim (${effectiveBase}h)`;
  } else if (isBE35h) {
    const base = 35;
    const deducted = holidaysInWeekCount * 7;
    theoreticalHours = base - deducted;
    theoreticalLabel = holidaysInWeekCount > 0
      ? `BE: 35h - ${holidaysInWeekCount}×7h férié = ${theoreticalHours}h`
      : 'Bureau d\'Étude (35h)';
  } else {
    theoreticalHours = getWeeklyTheoreticalHours(weekNumber);
    theoreticalLabel = isEvenWeek(weekNumber) ? 'Semaine Paire' : 'Semaine Impaire';
  }

  const hoursLuSa = totalNormal + totalSaturday;
  const effectiveHours = resolveWeeklyHours(overrideWeeklyHours, customWeeklyHours);
  const overtime = calculateWeeklyOvertime(weekNumber, hoursLuSa, totalSunday, hasGD || isBE35h, effectiveHours);

  // --- TABLE RÉCAP HEURES ---
  const finalY = (doc as unknown as JsPDFWithAutoTable).lastAutoTable.finalY + 6;
  const recapHoursBody: (string | number)[][] = [
    ['Heures Théoriques', `${theoreticalHours} h`],
    ['Total Heures Saisies', `${totalHoursWk} h`],
  ];
  if (overtime.overtime25 > 0) recapHoursBody.push(['   dont HS 25%', `${overtime.overtime25.toFixed(2)} h`]);
  if (overtime.overtime50 > 0) recapHoursBody.push(['   dont HS 50%', `${overtime.overtime50.toFixed(2)} h`]);
  if (overtime.sundayHours > 0) recapHoursBody.push(['   dont Dimanche (100%)', `${overtime.sundayHours.toFixed(2)} h`]);

  const labelAlreadyHasHours = theoreticalLabel.includes(`${theoreticalHours}h`);
  const hoursTitle = hasGD || labelAlreadyHasHours
    ? `HEURES - ${theoreticalLabel}`
    : `HEURES - ${theoreticalLabel} (${theoreticalHours}h)`;

  autoTable(doc, {
    startY: finalY, head: [[hoursTitle, '']], body: recapHoursBody,
    theme: 'plain',
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 8, fontStyle: 'bold' },
    styles: { fontSize: 8, cellPadding: 2 },
    columnStyles: { 0: { cellWidth: 70 }, 1: { cellWidth: 30, halign: 'right', fontStyle: 'bold' } },
    tableWidth: 100, margin: { left: 14 }
  });

  let currentY = (doc as unknown as JsPDFWithAutoTable).lastAutoTable.finalY + 3;

  // --- TABLE GRAND DÉPLACEMENT ---
  if (hasGD) {
    const recapGDBody: (string | number | string)[][] = [
      ['Chantier', gdStats.gdProjectName || 'Projet GD'],
      ['Nombre de jours', `${gdStats.totalGDDays} jour${gdStats.totalGDDays > 1 ? 's' : ''}`],
      ['Forfait journalier', `${gdStats.gdDailyRate.toFixed(2)} €/jour`],
      ['TOTAL INDEMNITÉ GD', `${(gdStats.gdDailyRate * gdStats.totalGDDays).toFixed(2)} €`],
    ];
    if (gdStats.totalGDTravelHours > 0) {
      recapGDBody.push(['Amplitude (trajet aller)', `${gdStats.totalGDTravelHours.toFixed(2)} h`]);
    }

    autoTable(doc, {
      startY: currentY, head: [['GRAND DÉPLACEMENT', '']], body: recapGDBody,
      theme: 'plain',
      headStyles: { fillColor: [220, 38, 38], textColor: 255, fontSize: 8, fontStyle: 'bold' },
      styles: { fontSize: 8, cellPadding: 2 },
      columnStyles: { 0: { cellWidth: 70 }, 1: { cellWidth: 30, halign: 'right', fontStyle: 'bold' } },
      tableWidth: 100, margin: { left: 14 },
      bodyStyles: { lineColor: [254, 202, 202], lineWidth: 0.1 },
      didParseCell: (data) => {
        if (data.row.index === 3 && data.section === 'body') {
          data.cell.styles.fillColor = [254, 226, 226];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });
    currentY = (doc as unknown as JsPDFWithAutoTable).lastAutoTable.finalY + 3;
  }

  // --- TABLE INDEMNITÉS ---
  const recapIndemnitiesBody: (string | number)[][] = [
    ['Paniers Repas', indemnityStats.totalMeals],
    ['Transports', indemnityStats.totalTransport],
    ['Trajets', indemnityStats.totalTrajet],
  ];
  const sortedZones = Object.entries(indemnityStats.zonesCount)
    .sort((a, b) => getZoneRank(a[0]) - getZoneRank(b[0]));
  if (sortedZones.length > 0) {
    sortedZones.forEach(([z, count]) => {
      if (z !== 'GD') recapIndemnitiesBody.push([`Zone ${z}`, `${count} jour${count > 1 ? 's' : ''}`]);
    });
  }

  autoTable(doc, {
    startY: currentY, head: [['INDEMNITÉS', '']], body: recapIndemnitiesBody,
    theme: 'plain',
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 8, fontStyle: 'bold' },
    styles: { fontSize: 8, cellPadding: 2 },
    columnStyles: { 0: { cellWidth: 70 }, 1: { cellWidth: 30, halign: 'right', fontStyle: 'bold' } },
    tableWidth: 100, margin: { left: 14 }
  });

  // --- SIGNATURES ---
  const signY = (doc as unknown as JsPDFWithAutoTable).lastAutoTable.finalY + 12;
  const signImageY = signY + 10;

  doc.setFontSize(9);
  doc.setTextColor(0);
  doc.setFont('helvetica', 'bold');
  doc.text("Signature Employé :", 14, signY);
  doc.setFont('helvetica', 'normal');

  if (employeeSignature) {
    try {
      doc.addImage(employeeSignature, 'PNG', 14, signImageY, 40, 28);
    } catch {
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text("Signature non disponible", 14, signImageY + 10);
    }
  }

  doc.setFont('helvetica', 'bold');
  doc.text("OGER ENERGIES", 120, signY);
  doc.setFont('helvetica', 'normal');
  doc.text("Jean-Luc Oger", 120, signY + 4);
  doc.text("Gérant :", 120, signY + 8);

  if (directionSignData) {
    doc.addImage(directionSignData, 'PNG', 120, signImageY, 28, 40);
  } else {
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text("En attente de validation", 120, signImageY + 20);
  }

  return doc;
}

// --- Utilitaire partagé pour le chargement d'images ---
export const optimizeImage = (url: string, maxWidth: number = 300): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const scale = Math.min(1, maxWidth / img.width);
      const w = img.width * scale;
      const h = img.height * scale;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/png'));
      } else {
        resolve('');
      }
    };
    img.onerror = () => resolve('');
    img.src = url;
  });
};

export const LOGO_URL = '/logo.png';
export const SIGN_URL = '/sign.png';
